import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/** Sessions de formation du mode OF (CDC §2.2 et §6.1). */
export interface ApprenantDeSession {
  id: string;
  nom: string;
  prenom: string;
  statut: string;
  statutLibelle: string;
  statutTon: string;
}

export interface LigneSession {
  id: string;
  libelle: string;
  site: { code: string; nom: string };
  formation: { code: string; titre: string; niveau: string; heures: number };
  dateDebut: string;
  dateFin: string;
  placesMax: number | null;
  /** `null` quand la session n'a pas de plafond de places. */
  placesRestantes: number | null;
  effectif: number;
  seances: number;
  /** Code d'émargement : propre à la session, hérité de la formation, ou absent. */
  codeEmargement: { source: 'SESSION' | 'FORMATION' | null; modifieLe: string | null };
  /**
   * Rappels d'émargement (TF-460) : coupés, ou les heures de la session
   * (`null` : celle de l'organisme) ; `applique` dit celles qui partent.
   */
  rappelEmargement: ReglageRappelSession & {
    applique: { matin: string | null; apresMidi: string | null };
    organisme: { matin: string; apresMidi: string };
  };
  apprenants: ApprenantDeSession[];
}

export interface ReglageRappelSession {
  actif: boolean;
  heureMatin: string | null;
  heureApresMidi: string | null;
}

export interface DossierAffectable {
  id: string;
  nom: string;
  prenom: string;
  site: string;
  formation: string | null;
  formationCode: string | null;
  statut: string;
  statutLibelle: string;
  statutTon: string;
}

export interface ReponseSessions {
  sessions: LigneSession[];
  /** Dossiers OF du périmètre encore sans session. */
  aRattacher: DossierAffectable[];
  formations: { code: string; titre: string; niveau: string }[];
  sites: string[];
  compteurs: {
    sessions: number;
    apprenantsRattaches: number;
    dossiersARattacher: number;
  };
}

export function useSessions(site: string) {
  return useQuery({
    queryKey: ['sessions', site],
    queryFn: () =>
      appelApi<ReponseSessions>(`/sessions?${new URLSearchParams({ site }).toString()}`),
  });
}

export interface EntreeCreationSession {
  formationCode: string;
  siteCode: string;
  libelle: string;
  dateDebut: string;
  dateFin: string;
  placesMax?: number;
}

export function useCreerSession(site: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeCreationSession) =>
      appelApi<LigneSession>('/sessions', { methode: 'POST', corps: entree }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sessions', site] });
    },
  });
}

export function useModifierSession(site: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      id: string;
      libelle?: string;
      dateDebut?: string;
      dateFin?: string;
      /** `null` retire le plafond de places. */
      placesMax?: number | null;
      /** Quatre chiffres pour le poser, `null` pour revenir au code de la formation. */
      codeEmargement?: string | null;
      rappelEmargement?: ReglageRappelSession;
    }) => {
      const { id, ...modifications } = variables;
      return appelApi<LigneSession>(`/sessions/${id}`, {
        methode: 'PATCH',
        corps: modifications,
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sessions', site] });
    },
  });
}

/**
 * Rattachement d'un dossier OF à une session.
 *
 * Le dossier et son parcours changent : les vues du dossier sont invalidées
 * aussi, pour que le jalon « session de formation créée » suive sans que la
 * personne ait à recharger l'écran.
 */
export function useRattacherApprenant(site: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { sessionId: string; apprenantId: string }) =>
      appelApi<LigneSession>(`/sessions/${variables.sessionId}/apprenants`, {
        methode: 'POST',
        corps: { apprenantId: variables.apprenantId },
      }),
    onSuccess: (_donnees, variables) => {
      void client.invalidateQueries({ queryKey: ['sessions', site] });
      void client.invalidateQueries({ queryKey: ['dossier', variables.apprenantId] });
      void client.invalidateQueries({ queryKey: ['documents', variables.apprenantId] });
    },
  });
}

export function useDetacherApprenant(site: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { sessionId: string; apprenantId: string }) =>
      appelApi<LigneSession>(
        `/sessions/${variables.sessionId}/apprenants/${variables.apprenantId}`,
        { methode: 'DELETE' },
      ),
    onSuccess: (_donnees, variables) => {
      void client.invalidateQueries({ queryKey: ['sessions', site] });
      void client.invalidateQueries({ queryKey: ['dossier', variables.apprenantId] });
      void client.invalidateQueries({ queryKey: ['documents', variables.apprenantId] });
    },
  });
}
